<template>
  <div class="summary-tab" data-testid="commentary-summary-tab">
    <div class="mt-2" v-if="!bulksubmit">
      <div class="font-size-md">{{ selectedcommentary.__code }}</div>
      <div class="font-size-md mb-2">{{ glCodeName }}</div>
    </div>
    <div class="d-flex gap-4 flex-column flex-md-row">
      <div class="summary-column" data-testid="commentary-summary-tab__proforma-column">
        <div class="font-size-md pl-2 noTransform" :style="`border-left: 4px solid ${tablecolors.heading.a} !important`">
          {{
            (cycletype === 'Forecast' ? forecastLabel : actualLabel) +
            ' Vs ' +
            proformaLabel
          }}
        </div>
        <div v-if="loadingtext1" class="mt-5 pa-5">
          <v-skeleton-loader type="paragraph,paragraph,paragraph,paragraph" class="skeleton-textarea"></v-skeleton-loader>
        </div>
        <div v-else>
          <lb-textarea class="mt-2 d-none" v-model="aitextModel"></lb-textarea>
          <div v-if="canEditSummary" data-testid="commentary-summary-tab__proforma-editor">
            <v-textarea class="mt-2" height="250" outlined label="Summary" v-model="summaryAiProformaModel" />
          </div>
          <div v-else class="grey--text font-weight-semibold mt-2">No Summary</div>
        </div>
        <v-btn
          v-if="canTriggerSummaryAi"
          color="primary"
          class="mt-3"
          :disabled="isTyping || loadingtext1 || loadingtext2 || isThresholdExceedJVSummarisation"
          @click="emitGenerateSummary('One', 'proforma')"
          data-testid="commentary-summary-tab__gen-ai-proforma"
        >
          <v-icon dark left>mdi-creation</v-icon>
          <span class="noTransform">Gen AI</span>
        </v-btn>
      </div>
      <div class="summary-column" data-testid="commentary-summary-tab__forecast-column">
        <div class="font-size-md pl-2 noTransform" :style="`border-left: 4px solid ${tablecolors.heading.b} !important`">
          {{
            (cycletype === 'Forecast' ? forecastLabel : actualLabel) +
            ' Vs ' +
            (cycletype === 'Forecast' ? `Previous ${forecastLabel}` : forecastLabel)
          }}
        </div>
        <div v-if="loadingtext2" class="mt-5 pa-5">
          <v-skeleton-loader type="paragraph,paragraph,paragraph,paragraph" class="skeleton-textarea"></v-skeleton-loader>
        </div>
        <div v-else>
          <lb-textarea class="mt-2 d-none" v-model="aitextModel"></lb-textarea>
          <div v-if="canEditSummary" data-testid="commentary-summary-tab__forecast-editor">
            <v-textarea class="mt-2" height="250" outlined label="Summary" v-model="summaryAiForecastModel" />
          </div>
          <div v-else class="grey--text font-weight-semibold mt-2">No Summary</div>
        </div>
        <v-btn
          v-if="canTriggerSummaryAi"
          color="primary"
          class="mt-3"
          :disabled="isTyping || loadingtext1 || loadingtext2 || isThresholdExceedJVSummarisation"
          @click="emitGenerateSummary('Two', 'forecast')"
          data-testid="commentary-summary-tab__gen-ai-forecast"
        >
          <v-icon dark left>mdi-creation</v-icon>
          <span class="noTransform">Gen AI</span>
        </v-btn>
      </div>
    </div>
    <v-tooltip right content-class="tooltip-right" v-if="isThresholdExceedJVSummarisation">
      <template v-slot:activator="{ on, attrs }">
        <v-icon v-bind="attrs" v-on="on" class="mt-3">mdi-help-circle-outline</v-icon>
      </template>
      <div class="text-center">Hour limit exceeded : Please contact your administrator to increase the limit.</div>
    </v-tooltip>
  </div>
</template>

<script>
export default {
  name: 'CommentarySummaryTab',
  props: {
    bulksubmit: { type: Boolean, default: false },
    selectedcommentary: { type: Object, default: () => ({}) },
    tablecolors: {
      type: Object,
      default: () => ({ heading: { a: '', b: '' }, body: { a: '', b: '' } }),
    },
    cycletype: { type: String, default: '' },
    bulktype: { type: String, default: '' },
    summaryAiProforma: { type: String, default: '' },
    summaryAiForecast: { type: String, default: '' },
    aitext: { type: String, default: '' },
    loadingtext1: { type: Boolean, default: false },
    loadingtext2: { type: Boolean, default: false },
    isTyping: { type: Boolean, default: false },
    isThresholdExceedJVSummarisation: { type: Boolean, default: false },
    showSummaryTab: { type: Boolean, default: false },
    forceSummaryTab: { type: Boolean, default: false },
    ispreparer: { type: Boolean, default: false },
    actualLabel: { type: String, default: 'Actual' },
    proformaLabel: { type: String, default: 'Proforma' },
    forecastLabel: { type: String, default: 'Forecast' },
  },
  emits: ['update:summaryAiProforma', 'update:summaryAiForecast', 'update:aitext', 'generate-ai-summary'],
  computed: {
    glCodeName() {
      return (this.selectedcommentary && this.selectedcommentary.glcode_detail && this.selectedcommentary.glcode_detail.name) || '';
    },
    summaryAiProformaModel: {
      get() {
        return this.summaryAiProforma;
      },
      set(val) {
        this.$emit('update:summaryAiProforma', val);
      },
    },
    summaryAiForecastModel: {
      get() {
        return this.summaryAiForecast;
      },
      set(val) {
        this.$emit('update:summaryAiForecast', val);
      },
    },
    aitextModel: {
      get() {
        return this.aitext;
      },
      set(val) {
        this.$emit('update:aitext', val);
      },
    },
    effectiveShowSummaryTab() {
      if (this.bulksubmit) {
        return false;
      }
      const baseCondition =
        this.$store.state.aiEnabled &&
        this.showSummaryTab &&
        (this.isActualJVCommentaryValidForLocal(this.selectedcommentary) ||
          this.isForecastJVCommentaryValidForLocal(this.selectedcommentary));
      return this.forceSummaryTab || baseCondition;
    },
    canTriggerSummaryAi() {
      if (!this.effectiveShowSummaryTab) {
        return false;
      }
      return (
        this.$store.state.aiEnabled &&
        (this.selectedcommentary.__myaction === 'localadd' ||
          this.selectedcommentary.__myaction === 'regionaladd' ||
          this.selectedcommentary.__myaction === 'localvoluntary' ||
          this.selectedcommentary.__myaction === 'regionalvoluntary' ||
          (this.ispreparer && this.bulksubmit))
      );
    },
    canEditSummary() {
      if (!this.effectiveShowSummaryTab) {
        return false;
      }
      return (
        this.$store.state.aiEnabled &&
        this.showSummaryTab &&
        (this.isActualJVCommentaryValidForLocal(this.selectedcommentary) ||
          this.isForecastJVCommentaryValidForLocal(this.selectedcommentary))
      );
    },
  },
  watch: {
    effectiveShowSummaryTab(val) {
      if (!val) {
        this.summaryAiProformaModel = '';
        this.summaryAiForecastModel = '';
      }
    },
  },
  methods: {
    emitGenerateSummary(slot, mode) {
      this.$emit('generate-ai-summary', { commentary: this.selectedcommentary, slot, mode });
    },
    isActualJVCommentaryValidForLocal() {
      return true;
    },
    isForecastJVCommentaryValidForLocal() {
      return true;
    },
  },
};
</script>

<style scoped>
.summary-tab {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.summary-column {
  flex: 1;
  min-width: 260px;
}

.noTransform {
  text-transform: none;
}

@media (max-width: 960px) {
  .summary-column {
    width: 100%;
  }
}
</style>
